// CalendarView's title + toolbar block. Lifted out of the main
// component so the entry file stays under the 500-line cap. Renders:
//   • H1 with event-count Badge
//   • doc-ui Segmented for view switching
//   • optional consumer toolbar extras
//   • optional ICS export button
//   • optional desktop "New event" CTA (mobile uses the FAB instead)

import type { ReactNode } from 'react';
import {
  Badge,
  Button,
  Segmented,
  Toolbar,
} from '@viquard/doc-ui/components';
import { IcnPlus } from '@viquard/doc-ui/icons';
import type { CalendarEvent, ViewMode } from './types.js';
import { formatEventCount, type CalendarMessages } from './i18n.js';
import { ICSExportButton } from './components/ICSExportButton.js';
import { icsFilenameStem } from './calendar-view-props.js';

export interface CalendarViewToolbarProps {
  readonly title: string;
  readonly showTitle: boolean;
  readonly messages: CalendarMessages;
  readonly filteredEvents: readonly CalendarEvent[];
  readonly viewOptions: readonly { value: string; label: string }[];
  readonly view: ViewMode | string;
  readonly onViewChange: (next: ViewMode | string) => void;
  readonly toolbarExtras?: ReactNode | undefined;
  readonly enableICSExport: boolean;
  readonly showCreateButton: boolean;
  readonly isMobile: boolean;
  readonly onCreateEvent?: (() => void) | undefined;
}

export function CalendarViewToolbar(props: CalendarViewToolbarProps): ReactNode {
  const {
    title,
    showTitle,
    messages,
    filteredEvents,
    viewOptions,
    view,
    onViewChange,
    toolbarExtras,
    enableICSExport,
    showCreateButton,
    isMobile,
    onCreateEvent,
  } = props;

  return (
    <div className={showTitle ? 'vq-cal__topbar' : 'vq-cal__topbar vq-cal__topbar--tools-only'}>
      {showTitle ? (
        <div className="vq-cal__title-block">
          <h1 className="vq-cal__title">{title}</h1>
          <Badge
            tone="neutral"
            size="md"
            numeric
            label={formatEventCount(messages, filteredEvents.length)}
          >
            {filteredEvents.length}
          </Badge>
        </div>
      ) : null}
      <Toolbar
        aria-label={messages.controlsAriaLabel}
        size="sm"
        className="vq-cal__toolbar"
      >
        <Segmented
          options={viewOptions}
          value={view}
          onChange={onViewChange}
          label={messages.viewSwitcherAriaLabel}
          size="sm"
        />
        {/* On phones we drop the "New event" toolbar button because
            the FAB replaces it (and would otherwise duplicate the
            affordance), but ICS export and consumer-injected
            `toolbarExtras` stay visible — `styles.css` lets the
            toolbar horizontally scroll on ≤720 and wrap on ≤480 so
            there is room without horizontal overflow. */}
        {toolbarExtras}
        {enableICSExport && filteredEvents.length > 0 && (
          <ICSExportButton
            events={filteredEvents}
            filename={`${icsFilenameStem(title)}.ics`}
            calendarName={title}
          />
        )}
        {showCreateButton && !isMobile && onCreateEvent !== undefined && (
          <Button
            variant="primary"
            size="sm"
            iconLeft={<IcnPlus size={14} />}
            onClick={onCreateEvent}
          >
            {messages.newEvent}
          </Button>
        )}
      </Toolbar>
    </div>
  );
}
